import { useEffect, useState } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { Info, Flame } from "lucide-react";

const TONE_TOKENS = {
  pink:    { stroke: "#FE0979", glow: "rgba(254, 9, 121, 0.35)", text: "text-[#FE0979]", ring: "ring-[#FE0979]/40" },
  cyan:    { stroke: "#00F2FE", glow: "rgba(0, 242, 254, 0.30)", text: "text-[#00F2FE]", ring: "ring-[#00F2FE]/40" },
  emerald: { stroke: "#34D399", glow: "rgba(52, 211, 153, 0.28)", text: "text-emerald-400", ring: "ring-emerald-400/40" },
  amber:   { stroke: "#F59E0B", glow: "rgba(245, 158, 11, 0.28)", text: "text-amber-400", ring: "ring-amber-400/40" },
  zinc:    { stroke: "#A1A1AA", glow: "rgba(161, 161, 170, 0.25)", text: "text-zinc-400", ring: "ring-zinc-400/30" },
};

const SUB_LABELS = {
  visual_appeal: "Visual appeal",
  social_trend: "Social trend",
  uniqueness: "Uniqueness",
  creator_usability: "Creator usability",
  lighting: "Lighting",
};

const DEFAULT_TOOLTIP =
  "Viral Score estimates how strong this location is for social-first travel content based on aesthetics, trend potential, uniqueness and creator usability.";

/**
 * ViralScore — circular progress + colour-coded label + tooltip
 * size: "sm" | "md" | "lg"
 * showBreakdown: render the 5 weighted bars below
 * showLabel: render the tier label next to the ring
 */
export const ViralScore = ({
  breakdown,
  fallbackScore,
  size = "md",
  showLabel = true,
  showBreakdown = false,
  className = "",
  testId = "viral-score",
}) => {
  // Animate ring progress on mount
  const target = breakdown?.score ?? fallbackScore ?? 0;
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setProgress(target), 60);
    return () => clearTimeout(t);
  }, [target]);

  const tone = TONE_TOKENS[breakdown?.tone] || TONE_TOKENS.cyan;
  const dims = size === "lg" ? 96 : size === "sm" ? 44 : 64;
  const stroke = size === "lg" ? 8 : size === "sm" ? 4 : 6;
  const radius = (dims - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - progress / 100);

  const valueClass = size === "lg" ? "text-2xl" : size === "sm" ? "text-[11px]" : "text-base";
  const labelText = breakdown?.label;

  return (
    <TooltipProvider delayDuration={150}>
      <div
        data-testid={testId}
        data-viral-score={target}
        data-viral-label={labelText || ""}
        className={`inline-flex items-center gap-3 ${className}`}
      >
        {/* RING */}
        <div className="relative shrink-0" style={{ width: dims, height: dims }}>
          <svg width={dims} height={dims} viewBox={`0 0 ${dims} ${dims}`} className="-rotate-90">
            <circle
              cx={dims / 2}
              cy={dims / 2}
              r={radius}
              fill="none"
              stroke="rgba(255,255,255,0.08)"
              strokeWidth={stroke}
            />
            <circle
              cx={dims / 2}
              cy={dims / 2}
              r={radius}
              fill="none"
              stroke={tone.stroke}
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              style={{
                transition: "stroke-dashoffset 1.1s cubic-bezier(.2,.7,.2,1)",
                filter: `drop-shadow(0 0 6px ${tone.glow})`,
              }}
            />
          </svg>
          <div className="absolute inset-0 grid place-items-center">
            <div className={`font-display font-black ${valueClass} leading-none ${tone.text}`}>{target}</div>
          </div>
        </div>

        {/* LABEL + TOOLTIP */}
        {showLabel && (
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className={`text-[10px] font-bold uppercase tracking-[0.22em] ${tone.text}`}>
                {labelText || "Viral Score"}
              </span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    data-testid={`${testId}-info`}
                    aria-label="What is the Viral Score?"
                    className="text-zinc-400 hover:text-white transition"
                  >
                    <Info className="h-3 w-3" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  align="center"
                  className="max-w-[260px] glass-strong border border-white/10 text-zinc-100 text-xs leading-relaxed"
                >
                  {breakdown?.tooltip || DEFAULT_TOOLTIP}
                </TooltipContent>
              </Tooltip>
            </div>
            {breakdown?.explanation && (
              <p className="text-[11px] text-zinc-300 mt-0.5 leading-snug max-w-[260px]">
                {breakdown.explanation}
              </p>
            )}
            {!breakdown && (
              <p className="text-[11px] text-zinc-400 mt-0.5">Viral Score · 0–100</p>
            )}
          </div>
        )}
      </div>

      {/* BREAKDOWN BARS */}
      {showBreakdown && breakdown?.breakdown && (
        <div className="mt-4 grid grid-cols-1 gap-2.5" data-testid={`${testId}-breakdown`}>
          {Object.entries(breakdown.breakdown).map(([key, value]) => (
            <BreakdownBar
              key={key}
              label={SUB_LABELS[key] || key}
              weight={breakdown.weights?.[key] || 0}
              value={value}
              tone={tone}
            />
          ))}
        </div>
      )}
    </TooltipProvider>
  );
};

const BreakdownBar = ({ label, weight, value, tone }) => {
  const [w, setW] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setW(value), 80);
    return () => clearTimeout(t);
  }, [value]);

  return (
    <div data-testid={`viral-bar-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.2em]">
        <span className="text-zinc-300">
          {label}
          <span className="text-zinc-500 ml-1.5 font-medium normal-case tracking-normal">
            {Math.round((weight || 0) * 100)}%
          </span>
        </span>
        <span className={tone.text}>{value}</span>
      </div>
      <div className="mt-1 h-1.5 w-full rounded-full bg-white/5 overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{
            width: `${w}%`,
            background: tone.stroke,
            boxShadow: `0 0 8px ${tone.glow}`,
            transition: "width 1.0s cubic-bezier(.2,.7,.2,1)",
          }}
        />
      </div>
    </div>
  );
};

/**
 * Compact pill version — useful on cards. Just the score + flame icon, color-coded by tier.
 */
export const ViralScoreBadge = ({ breakdown, fallbackScore, className = "", testId = "viral-badge" }) => {
  const score = breakdown?.score ?? fallbackScore ?? 0;
  const tone = TONE_TOKENS[breakdown?.tone] || TONE_TOKENS.cyan;
  return (
    <span
      data-testid={testId}
      data-viral-score={score}
      data-viral-label={breakdown?.label || ""}
      className={`glass rounded-full px-2 py-1 inline-flex items-center gap-1 ring-1 ${tone.ring} ${className}`}
      title={breakdown?.label || "Viral Score"}
    >
      <Flame className={`h-3 w-3 ${tone.text}`} />
      <span className={`font-display font-bold text-[11px] ${tone.text}`}>{score}</span>
    </span>
  );
};
